<script setup lang="ts">
import type { EChartsOption } from 'echarts'
import type {
  IMonitorNbkrResponse,
  MonitorNbkrRange,
  MonitorNbkrTerm
} from '~/components/widget/monitor'
import {
  MONITOR_NBKR_CREDIT_CATEGORIES,
  MONITOR_NBKR_TERMS,
  MONITOR_NBKR_DEFAULT_TERM
} from '~/components/widget/monitor'
import { LazyVChart } from '~/components/ui/chart'

const NBKR_RANGES: MonitorNbkrRange[] = ['6m', '1y', '2y', '3y', 'all']

// API term ids contain "-" and "+", which are not valid in translation keys.
const NBKR_TERM_LABEL_KEYS: Record<MonitorNbkrTerm, string> = {
  all: 'monitor.nbkr.term_all',
  'up-to-1y': 'monitor.nbkr.term_up_to_1y',
  '0-1m': 'monitor.nbkr.term_0_1m',
  '1-3m': 'monitor.nbkr.term_1_3m',
  '3-6m': 'monitor.nbkr.term_3_6m',
  '6-12m': 'monitor.nbkr.term_6_12m',
  '1-3y': 'monitor.nbkr.term_1_3y',
  '3y+': 'monitor.nbkr.term_3y_plus'
}

const props = defineProps<{
  product: 'credit' | 'deposit'
  category?: string
}>()

const { t } = useI18n()

const selectedRange = ref<MonitorNbkrRange>('3y')

const terms = computed(() => MONITOR_NBKR_TERMS[props.product])
const selectedTerm = ref<MonitorNbkrTerm>(MONITOR_NBKR_DEFAULT_TERM[props.product])

// NBKR only publishes sectors for a subset of credit types; the rest have no series at all.
const hasNbkrSector = computed(() => {
  if (props.product === 'deposit') return true

  return MONITOR_NBKR_CREDIT_CATEGORIES.some((item) => item === props.category)
})

const requestParams = computed(() => ({
  product: props.product,
  range: selectedRange.value,
  term: selectedTerm.value,
  ...(props.product === 'credit' && props.category ? { category: props.category } : {})
}))

const { data: response, status } = useApi<IMonitorNbkrResponse>('/monitor/nbkr-rates', {
  params: requestParams,
  key: computed(
    () =>
      `monitor-nbkr-${props.product}-${props.category ?? 'all'}-${selectedTerm.value}-${selectedRange.value}`
  ),
  watch: [requestParams]
})

const points = computed(() => response.value?.data?.points ?? [])
const meta = computed(() => response.value?.meta)

const isLoading = computed(() => status.value === 'pending')
const hasPoints = computed(() => points.value.length > 0)

const hasRange = computed(() => meta.value?.has_range === true)

const preliminaryCount = computed(() => points.value.filter((point) => point.preliminary).length)

const rateFormatter = new Intl.NumberFormat('ru-RU', {
  minimumFractionDigits: 1,
  maximumFractionDigits: 2
})

const formatRate = (value: number) => `${rateFormatter.format(value)}%`

// API sends "YYYY-MM"; the axis and tooltip show it as "MM.YYYY".
const formatMonth = (value: string) => {
  const [year, month] = value.split('-')

  return year && month ? `${month}.${year}` : value
}

// Static styling shared by every render of the chart — only `data` fields change
// per range/category switch, so this is built once instead of on every recompute.
const NBKR_AREA_STYLE = {
  color: {
    type: 'linear',
    x: 0,
    y: 0,
    x2: 0,
    y2: 1,
    colorStops: [
      { offset: 0, color: 'rgba(37, 99, 235, 0.22)' },
      { offset: 1, color: 'rgba(37, 99, 235, 0.02)' }
    ]
  }
}

// The min/max corridor is two stacked lines: an invisible base at `min` and a filled
// band of height `max - min` on top of it.
const NBKR_RANGE_SERIES_STATIC = {
  type: 'line',
  stack: 'nbkr-range',
  smooth: true,
  showSymbol: false,
  silent: true,
  connectNulls: false,
  lineStyle: { opacity: 0 }
}

const NBKR_CHART_STATIC_OPTION = {
  grid: { left: 8, right: 16, top: 24, bottom: 8, containLabel: true },
  xAxis: {
    type: 'category',
    boundaryGap: false,
    axisTick: { show: false },
    axisLine: { lineStyle: { color: '#e5e7eb' } },
    axisLabel: { fontSize: 11, color: '#6b7280' }
  },
  yAxis: {
    type: 'value',
    scale: true,
    axisLabel: { formatter: '{value}%' },
    splitLine: { lineStyle: { color: '#f3f4f6' } }
  },
  series: [
    {
      type: 'line',
      smooth: true,
      showSymbol: false,
      connectNulls: false,
      lineStyle: { width: 2.5, color: '#2563eb' }
    },
    {
      type: 'line',
      smooth: true,
      showSymbol: false,
      connectNulls: false,
      lineStyle: { width: 2.5, color: '#2563eb', type: 'dashed' }
    }
  ]
}

const chartOption = computed<EChartsOption>(() => {
  const months = points.value.map((point) => formatMonth(point.month))
  const confirmed = points.value.map((point) => (point.preliminary ? null : point.rate))

  // Preliminary months are drawn as a separate dashed series. NBKR may flag a month in
  // the middle of the series, not only the tail, so every preliminary point also pulls
  // in its confirmed neighbours: the dashed segment then bridges each gap in the solid line.
  const preliminary = points.value.map((point, index) => {
    const isBridge =
      point.preliminary ||
      points.value[index - 1]?.preliminary === true ||
      points.value[index + 1]?.preliminary === true

    return isBridge ? point.rate : null
  })

  const rangeBase = points.value.map((point) => (point.max === null ? null : point.min))
  const rangeSpan = points.value.map((point) =>
    point.min === null || point.max === null
      ? null
      : Math.round((point.max - point.min) * 100) / 100
  )

  // With the corridor shown the gradient under the line would blur into it, so the
  // line keeps its fill only when there is no corridor.
  const rateSeries = [
    {
      ...NBKR_CHART_STATIC_OPTION.series[0],
      ...(hasRange.value ? {} : { areaStyle: NBKR_AREA_STYLE }),
      data: confirmed
    },
    {
      ...NBKR_CHART_STATIC_OPTION.series[1],
      ...(hasRange.value ? {} : { areaStyle: NBKR_AREA_STYLE }),
      data: preliminary
    }
  ]

  const rangeSeries = hasRange.value
    ? [
        { ...NBKR_RANGE_SERIES_STATIC, data: rangeBase },
        {
          ...NBKR_RANGE_SERIES_STATIC,
          areaStyle: { color: 'rgba(37, 99, 235, 0.1)' },
          data: rangeSpan
        }
      ]
    : []

  return {
    ...NBKR_CHART_STATIC_OPTION,
    tooltip: {
      trigger: 'axis',
      formatter: (params: { dataIndex: number }[]) => {
        const dataIndex = params[0]?.dataIndex

        if (dataIndex === undefined) return ''

        const point = points.value[dataIndex]

        if (!point) return ''

        const rangeMark =
          point.min !== null && point.max !== null
            ? `<br/><span>${t('monitor.nbkr.range_hint', { min: formatRate(point.min), max: formatRate(point.max) })}</span>`
            : ''

        const preliminaryMark = point.preliminary
          ? `<br/><span>${t('monitor.nbkr.preliminary_mark')}</span>`
          : ''

        return `<strong>${formatMonth(point.month)}</strong><br/>${formatRate(point.rate)}${rangeMark}${preliminaryMark}`
      }
    },
    xAxis: { ...NBKR_CHART_STATIC_OPTION.xAxis, data: months },
    series: [...rangeSeries, ...rateSeries]
  }
})
</script>

<template>
  <div v-if="hasNbkrSector && (hasPoints || isLoading)" class="Card">
    <div class="mb-4 flex flex-wrap items-start justify-between gap-4">
      <div class="flex flex-col gap-1">
        <h2 class="text-lg font-semibold">{{ t('monitor.nbkr.title') }}</h2>
        <p class="text-muted-foreground text-sm">
          {{ t('monitor.nbkr.subtitle', { source: meta?.source ?? 'НБКР' }) }}
        </p>
      </div>

      <div class="flex flex-wrap gap-2">
        <Button
          v-for="range in NBKR_RANGES"
          :key="range"
          :variant="selectedRange === range ? 'blue' : 'outline-gray'"
          size="sm"
          @click="selectedRange = range"
        >
          {{ t(`monitor.nbkr.range_${range}`) }}
        </Button>
      </div>
    </div>

    <div class="mb-4 flex flex-wrap gap-2">
      <Button
        v-for="term in terms"
        :key="term"
        :variant="selectedTerm === term ? 'blue' : 'outline-gray'"
        size="sm"
        @click="selectedTerm = term"
      >
        {{ t(NBKR_TERM_LABEL_KEYS[term]) }}
      </Button>
    </div>

    <div v-if="isLoading" class="h-72 w-full animate-pulse rounded-lg bg-gray-100" />

    <template v-else>
      <LazyVChart
        :option="chartOption"
        autoresize
        class="h-72 w-full"
        :update-options="{ notMerge: true }"
      />

      <p v-if="hasRange" class="text-muted-foreground mt-2 text-xs">
        {{ t('monitor.nbkr.range_legend') }}
      </p>

      <p v-if="preliminaryCount" class="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-700">
        {{ t('monitor.nbkr.preliminary_note', { count: preliminaryCount }) }}
      </p>

      <div class="text-muted-foreground mt-3 flex flex-wrap gap-x-4 gap-y-1 border-t pt-3 text-xs">
        <span>{{ t('monitor.nbkr.source', { table: meta?.source_table ?? '—' }) }}</span>
        <span>{{ t('monitor.nbkr.points', { count: points.length }) }}</span>
        <span v-if="meta?.last_month">
          {{
            t('monitor.nbkr.last_month', {
              month: formatMonth(meta.last_month.month),
              rate: formatRate(meta.last_month.rate)
            })
          }}
        </span>
      </div>
    </template>
  </div>
</template>
